import { LloydsRoadmapPortfolioBuildingDTO } from 'csr-asset-app';

const ADDRESS_LINE_HEIGHT_PX = 28;
const CELL_PADDING_Y_PX = 8;

function rowHeightForLines(visualLines: number): number {
  return visualLines * ADDRESS_LINE_HEIGHT_PX + CELL_PADDING_Y_PX * 2;
}

export const ADDRESS_TEXT_MAX_WIDTH_PX = 250;
export const ENERGY_CONSUMPTION_ADDRESS_TEXT_MAX_WIDTH_PX = 180;
const AVG_CHAR_WIDTH_PX = 8;

const REFERENCE_ROW_HEIGHT_PX = rowHeightForLines(3);
export const FIRST_PAGE_TABLE_BODY_BUDGET_PX = 10 * REFERENCE_ROW_HEIGHT_PX;
export const ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX = 15 * REFERENCE_ROW_HEIGHT_PX;

/**
 * Legend sizing from DevTools on `.row.mt-4.g-4`:
 * - one item ~23px tall → 24px
 * - two wrapped rows → ~85.5px content → gap ≈ 85.5 − 2×24 = 37.5 → 36px
 * - first wrap line fits 4 items in print width
 * One legend row (+ mt-4) is already assumed outside the table-body budgets.
 */
const BUILDINGS_LEGEND_MARGIN_TOP_PX = 24; // mt-4 (cancels out of extra-reserve)
const BUILDINGS_LEGEND_ROW_HEIGHT_PX = 24;
const BUILDINGS_LEGEND_ROW_GAP_PX = 36;
const BUILDINGS_LEGEND_ITEMS_PER_ROW = 4;
const BUILDINGS_LEGEND_BASE_HEIGHT_PX = BUILDINGS_LEGEND_MARGIN_TOP_PX + BUILDINGS_LEGEND_ROW_HEIGHT_PX;

export function estimateBuildingsLegendHeightPx(itemCount: number): number {
  const rows = Math.max(1, Math.ceil(itemCount / BUILDINGS_LEGEND_ITEMS_PER_ROW));
  return (
    BUILDINGS_LEGEND_MARGIN_TOP_PX +
    rows * BUILDINGS_LEGEND_ROW_HEIGHT_PX +
    Math.max(0, rows - 1) * BUILDINGS_LEGEND_ROW_GAP_PX
  );
}

/** Extra height beyond the single legend row already baked into page budgets. */
export function buildingsLegendExtraReservePx(itemCount: number): number {
  return Math.max(0, estimateBuildingsLegendHeightPx(itemCount) - BUILDINGS_LEGEND_BASE_HEIGHT_PX);
}

export function estimateWrappedLineCount(text: string, maxWidthPx = ADDRESS_TEXT_MAX_WIDTH_PX): number {
  const trimmed = text?.trim();
  if (!trimmed) {
    return 0;
  }

  const words = trimmed.split(/\s+/);
  let lines = 1;
  let currentLineWidth = 0;
  const spaceWidth = AVG_CHAR_WIDTH_PX;

  for (const word of words) {
    const wordWidth = Math.max(word.length, 1) * AVG_CHAR_WIDTH_PX;
    if (currentLineWidth === 0) {
      currentLineWidth = wordWidth;
      if (wordWidth > maxWidthPx) {
        const extra = Math.ceil(wordWidth / maxWidthPx) - 1;
        lines += extra;
        currentLineWidth = wordWidth - extra * maxWidthPx;
      }
      continue;
    }

    if (currentLineWidth + spaceWidth + wordWidth <= maxWidthPx) {
      currentLineWidth += spaceWidth + wordWidth;
      continue;
    }

    lines += 1;
    currentLineWidth = wordWidth;
    if (wordWidth > maxWidthPx) {
      const extra = Math.ceil(wordWidth / maxWidthPx) - 1;
      lines += extra;
      currentLineWidth = wordWidth - extra * maxWidthPx;
    }
  }

  return lines;
}

export function estimateBuildingAddressVisualLines(
  displayAddress: string[] | undefined,
  maxWidthPx = ADDRESS_TEXT_MAX_WIDTH_PX
): number {
  if (!displayAddress?.length) {
    return 1;
  }

  const lines = displayAddress.reduce((sum, line) => sum + estimateWrappedLineCount(line, maxWidthPx), 0);
  return Math.max(lines, 1);
}

export function estimateBuildingRowHeightPx(
  displayAddress: string[] | undefined,
  maxWidthPx = ADDRESS_TEXT_MAX_WIDTH_PX
): number {
  return rowHeightForLines(estimateBuildingAddressVisualLines(displayAddress, maxWidthPx));
}

export function paginateBuildingsByRowHeight(
  buildings: LloydsRoadmapPortfolioBuildingDTO[],
  firstPageBudgetPx = FIRST_PAGE_TABLE_BODY_BUDGET_PX,
  additionalPageBudgetPx = ADDITIONAL_PAGE_TABLE_BODY_BUDGET_PX,
  addressTextMaxWidthPx = ADDRESS_TEXT_MAX_WIDTH_PX
): LloydsRoadmapPortfolioBuildingDTO[][] {
  const pages: LloydsRoadmapPortfolioBuildingDTO[][] = [];
  let currentPage: LloydsRoadmapPortfolioBuildingDTO[] = [];
  let usedHeight = 0;
  let budget = firstPageBudgetPx;

  for (const building of buildings) {
    const rowHeight = estimateBuildingRowHeightPx(building.displayAddress, addressTextMaxWidthPx);
    if (currentPage.length > 0 && usedHeight + rowHeight > budget) {
      pages.push(currentPage);
      currentPage = [];
      usedHeight = 0;
      budget = additionalPageBudgetPx;
    }
    currentPage.push(building);
    usedHeight += rowHeight;
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return pages;
}
